// The session: what a click means (PLAN-CLIENT-SPLIT §2.3).
//
// `useBoardInteraction` as a plain class. Click-to-select, click-to-move —
// with belt construction and knight re-aim folded into the same flow rather
// than sitting behind mode toggles. The modes were removed because both of
// their jobs were really *contexts*: a selected knight already implies
// re-aiming, and an empty square already implies construction. Carrying that
// as state meant a stale mode plus a stale direction could turn a click into
// a different action than the one intended, with no feedback.
//
// The store rejects anything illegal, so this only decides what a click
// *means*. Where it can tell in advance that a click cannot mean anything, it
// says so through the refusal instead of silently doing nothing.
//
// **One class, seven modules.** Each of the old sub-hooks is a module of
// verbs over the shared `SessionCore`; this file is the composer and the
// click itself. `interaction()` is the object the chrome reads today — the
// old hook's fifty keys, unchanged — and `view()` is the contract's
// `SessionView`: the board's half since day 5, the chrome's (`chrome.ts`)
// since day 6.

import type {
  BoardHighlights,
  CarryPoint,
  Direction as DirectionName,
  OptionId,
  Outcome,
  ShellContext,
  Session as SessionApi,
  SessionMode,
  SessionView,
  Square,
  ToolId
} from "src/api";
import type { GameStore } from "src/engine/game/GameStore";
import { asSquare, PIECE_NAME, STRUCTURE_NAME } from "src/engine/api/names";
import { CARD_TABLE } from "src/engine/rules/campaign/cards";
import { isGameOver } from "src/engine/rules/chess/status";
import { bestBeltRoute, canPlaceBelt } from "src/engine/rules/industry/belts";
import { hasStructure, structureAt } from "src/engine/rules/industry/state";
import { moveFrom, moveTarget } from "src/engine/rules/move";
import { hasCell, pieceAt } from "src/engine/rules/position";
import { type Color, type Direction, pieceColor, pieceType, WHITE } from "src/shared/board";
import { selectionVerbs } from "./actions";
import { armingVerbs } from "./arming";
import { beltVerbs } from "./belts";
import { carryVerbs } from "./carrying";
import { Chrome } from "./chrome";
import { SessionCore, type SessionEmit, type SessionTransport } from "./core";
import { discardVerb } from "./discarding";
import { followSelection } from "./follow";
import { groundModel } from "./ground";
import {
  aimMarksOf,
  beltPreviewFor,
  buildHighlights,
  lineEndsFrom,
  NO_HIGHLIGHTS,
  paveRing,
  reAimTargetsOf,
  selectedMoves,
  sourcesFor
} from "./marks";
import { CARD_REFUSAL, PLACEMENT_REFUSAL, REFUSAL } from "./refusals";
import { actRefusal } from "./turn";
import { trainClick } from "./trains";
import { boostSourceFor, freeMoveRefusal, pieceMoveRefusal } from "src/engine/rules/clock";
import type { Command, GameState } from "src/engine/rules/commands";

export type { SessionEmit, SessionTransport };

/** No extent to draw, as one identity. */
const NO_MARKS: readonly number[] = [];

/** The four heading names, indexed by the packed direction. */
const DIR_INDEX: Record<DirectionName, Direction> = { N: 0, E: 1, S: 2, W: 3 };

/**
 * A move as the player means it: through the pools, else as an Empower
 * network's extra move (§33 S1), the lowest source when they overlap.
 */
function moveCommand(state: GameState, from: number, to: number): Command {
  const boost = boostSourceFor(state, from);
  return boost === undefined ? { type: "move", from, to } : { type: "move", from, to, boost };
}

export class Session implements SessionApi {
  private readonly arming;
  private readonly selection;
  private readonly belts;
  private readonly carrying;
  private readonly discard_;
  /** The chrome's half of the view, and what each control in it does. */
  private readonly chrome;
  private unsubscribe: () => void;

  /** The derived bag, rebuilt when the revision moves. */
  private memo: { revision: number; derived: Derived } | null = null;

  constructor(readonly core: SessionCore) {
    this.arming = armingVerbs(core);
    this.selection = selectionVerbs(core);
    this.belts = beltVerbs(core);
    this.discard_ = discardVerb(core, {
      eraseBelt: this.belts.eraseBelt,
      demolish: this.arming.demolish
    });
    this.carrying = carryVerbs(core, {
      armForge: this.arming.armForge,
      armBuild: this.arming.armBuild,
      beginPlace: this.arming.beginPlace,
      pickSquare: (square) => this.pickSquare(square)
    });
    this.chrome = new Chrome(core, {
      arming: this.arming,
      selection: this.selection,
      carrying: this.carrying,
      setMintFacing: (facing) => this.setMintFacing(facing)
    });
    // The selection follows its piece, and every store change is a change.
    this.unsubscribe = core.store.subscribe(() => {
      followSelection(core);
      core.changed();
    });
  }

  dispose(): void {
    this.unsubscribe();
  }

  // --- what the client tells it -----------------------------------------

  /** The one side this client may play, null for hot-seat, undefined to ask the room. */
  setSeat(side: Color | null | undefined): void {
    if (this.core.seatSide === side) return;
    this.core.seatSide = side;
    this.core.changed();
  }

  /** Is the Build tool up? See `SessionCore.building`. */
  setBuilding(building: boolean): void {
    if (this.core.building === building) return;
    this.core.building = building;
    followSelection(this.core);
    this.core.changed();
  }

  tool(tool: ToolId): void {
    this.setBuilding(tool === "build");
  }

  shell(context: ShellContext): void {
    this.chrome.setShell(context);
  }

  choose(option: OptionId): Outcome {
    return this.chrome.choose(option);
  }

  lift(option: OptionId, at: CarryPoint): Outcome {
    return this.chrome.lift(option, at);
  }

  // --- the board's own verbs (PLAN-CLIENT-SPLIT day 8) -----------------------
  // Each is one of the interaction bag's entries, named for the gesture; the
  // canvas reads nothing else.

  doubleTap(square: Square): void {
    this.selection.doubleClickSquare(square);
  }

  removePicked(): void {
    this.belts.removePicked();
  }

  beginPath(square: Square): boolean {
    return this.belts.startPath(square);
  }

  extendPath(square: Square): void {
    this.belts.extendPath(square);
  }

  endPath(): void {
    this.belts.commitPath();
  }

  discard(square: Square, press: boolean): void {
    this.discard_(square, press);
  }

  canCarry(square: Square): boolean {
    return this.carrying.canCarry(square);
  }

  carry(square: Square, at: CarryPoint): void {
    this.carrying.carryPiece(square, at);
  }

  drop(square: Square | -1): void {
    this.carrying.dropCarry(square);
  }

  attach(transport: SessionTransport | null): void {
    this.core.attach(transport);
  }

  /** Bound, so `useSyncExternalStore` may take it unbound. */
  subscribe = (listener: () => void): (() => void) => this.core.subscribe(listener);

  get revision(): number {
    return this.core.revision;
  }

  // --- the click ----------------------------------------------------------

  /**
   * The pointer moved to a new square.
   *
   * Swallowed unless a run is anchored and waiting for its far end — that is
   * the only thing that reads it, and reporting every square the pointer
   * crosses would rebuild the view continuously for the whole game.
   */
  hover(square: number): void {
    const core = this.core;
    if (core.lineAnchor === undefined) return;
    const next = square < 0 ? undefined : square;
    if (core.hovered === next) return;
    core.hovered = next;
    core.changed();
  }

  /**
   * A click that landed on no square at all — past the edge of the board, or
   * on the background beyond it.
   *
   * It means what a click on a square that offers nothing already means, and
   * peels the same one layer per click: the arrows, then a card or a forge
   * waiting for a target, then a half-drawn run, then the selection. Matching
   * that order matters more than the order itself — "click away" doing more
   * off the board than on it is a second gesture wearing the same name.
   */
  dismiss(): void {
    const core = this.core;
    core.setRefusal(null);
    core.cycle = undefined;
    if (core.aiming) core.aiming = undefined;
    else if (core.forging) core.forging = undefined;
    else if (core.placing) core.placing = undefined;
    else if (core.pending) core.pending = undefined;
    else if (core.lineAnchor !== undefined) core.lineAnchor = undefined;
    else core.selected = undefined;
    core.changed();
  }

  cancel(): void {
    this.dismiss();
  }

  tap(square: Square): void {
    this.pickSquare(square);
  }

  face(direction: DirectionName, options?: { step?: boolean }): void {
    this.pickDirection(DIR_INDEX[direction], options?.step);
  }

  /** A structure says its name, quietly, when the selection *changes* to it. */
  private voiceStructure(square: number): void {
    const core = this.core;
    if (core.selected === square) return;
    const structure = structureAt(core.state.industry, square);
    if (structure) core.cue({ kind: "voice", structure: STRUCTURE_NAME[structure.kind] });
  }

  pickSquare(square: number): void {
    const core = this.core;
    try {
      this.click(square);
    } finally {
      core.changed();
    }
  }

  private click(square: number): void {
    const core = this.core;
    const { state, localSide, controlsTurn } = core;
    if (isGameOver(state.status)) return;
    // **Not even a selection while looking back** (§29 U4). The board on
    // screen is an earlier one and this reasons about the live board, so a
    // piece picked up here is a piece on a square the player cannot see.
    if (core.viewing) {
      core.setRefusal(REFUSAL["viewing-history"]);
      return;
    }
    core.setRefusal(null);
    const d = this.derived();

    // A train's verbs (§31 S7): drive, board, alight — before the building
    // branch, since track and cars are buildings.
    if (trainClick(core, square)) return;

    // **A building's square with nobody on it** (§31 S1: it stands on a
    // cell). The selected piece may take it, plow it or stand on it — any
    // move named by this square — else the click selects the building, which
    // the shelf has plenty to say about. A piece standing on one is a piece.
    if (hasStructure(state.industry, square) && pieceAt(state.position, square) === 0) {
      // **A third meaning since §26 F5: the selected piece can take it.**
      // `moveTarget`, never `moveTo` (§27): a building has health now, so a
      // blow that does not finish it is a bash whose `to` is where the
      // *attacker* stops.
      const capture = d.movesFromSelected.find((m) => moveTarget(m) === square);
      if (capture !== undefined) {
        // Promotion piece is omitted: the reducer defaults to a queen. Not
        // cleared: the follow re-anchors the selection to where it landed.
        core.send(moveCommand(state, moveFrom(capture), square));
        return;
      }
      core.lineAnchor = undefined;
      this.voiceStructure(square);
      core.selected = square;
      return;
    }

    // The other thing it can mean: Bridges tiles are banked and this is where
    // one goes. A card aimed at an off-board square falls *through* to the
    // aiming branch, or the card dispatch has a second implementation.
    const aimedHere = core.aiming?.at.includes(square) ?? false;
    if (!hasCell(state.position, square) && !aimedHere) {
      if (!d.paving || core.aiming) return;
      if (!core.send({ type: "pave", squares: [square] })) {
        core.setRefusal("A bridge has to join two banks, and may not open a line onto your own king.");
      }
      return;
    }

    // A card is waiting for a target. Clicking a candidate plays it; clicking
    // anywhere else cancels, which is the same bargain the forge makes.
    const aiming = core.aiming;
    if (aiming) {
      if (aiming.power === "sacrifice") {
        if (aiming.at.includes(square)) core.send({ type: "sacrifice", square });
        core.aiming = undefined;
        return;
      }
      if (aiming.at.includes(square)) {
        // The second square, if this card wants one. `target` already set
        // means we are picking the *source* now.
        if (aiming.target !== undefined) {
          const played = core.send({
            type: "play-card",
            instance: aiming.instance,
            target: aiming.target,
            source: square
          });
          if (played) core.cue({ kind: "card-played" });
          core.aiming = undefined;
          return;
        }
        // A card that cannot resolve on this square alone is asking for a
        // pair, so light the neighbours it would accept and wait.
        const held = state.run?.deck.find((entry) => entry.id === aiming.instance);
        const card = held ? CARD_TABLE[held.card] : undefined;
        if (card && card.refuse(state, { target: square }) !== null) {
          const sources = sourcesFor(card, state, square);
          if (sources.length > 0) {
            core.aiming = { ...aiming, target: square, at: sources };
            return;
          }
        }
        if (core.send({ type: "play-card", instance: aiming.instance, target: square })) {
          core.cue({ kind: "card-played" });
        }
      }
      core.aiming = undefined;
      return;
    }

    // A forge is waiting for somewhere to put the piece.
    const forging = core.forging;
    if (forging) {
      if (forging.at.includes(square)) {
        // Which machine acts is a property of the *square*: the lead is
        // looked up rather than remembered from a selection.
        const lead = forging.leads?.get(square) ?? forging.square;
        if (forging.build !== undefined) {
          if (lead !== undefined) {
            core.send({ type: "build", square: lead, at: square, kind: forging.build, owner: localSide });
          }
        } else if (forging.type !== undefined) {
          // **The facing is omitted when nobody chose one**, never sent as
          // `null`: an explicit absence leaves the foundry's policy, then
          // `defaultMintFacing`, in charge.
          core.send({
            type: "forge",
            square: lead,
            target: forging.type,
            at: square,
            ...(core.mintFacing === null ? {} : { facing: core.mintFacing })
          });
        }
      }
      core.forging = undefined;
      return;
    }

    // A token placement in progress: a click on a lit square stands the
    // structure, anywhere else puts the tool down — silent, nothing was refused.
    const placing = core.placing;
    if (placing) {
      if (placing.at.includes(square)) core.send({ type: "place-structure", kind: placing.kind, at: square });
      core.placing = undefined;
      return;
    }
    // Whose turn it is changes what a square even offers, so a cycle from
    // before the turn flipped is meaningless.
    if (core.cycle && core.cycle.square !== square) core.cycle = undefined;

    // A square click while the arrows are open is a cancel. The arrows
    // themselves are `pickDirection`, which runs first.
    if (core.pending) {
      core.pending = undefined;
      core.cycle = undefined;
      return;
    }

    // A run is half-drawn. It completes on any *empty* square — a run may be
    // any shape that feeds through itself. Empty, though: letting a run end
    // on an occupied square meant clicking your own piece laid a lane at it.
    const lineAnchor = core.lineAnchor;
    if (lineAnchor !== undefined && pieceAt(state.position, square) === 0) {
      // The anchor was set on your turn; the opponent may have moved since.
      if (!controlsTurn) {
        core.lineAnchor = undefined;
        core.setRefusal(REFUSAL["not-your-turn"]);
        return;
      }
      if (square === lineAnchor) {
        // A run of one has no direction of its own, so fall back to asking.
        core.lineAnchor = undefined;
        core.pending = { kind: "lay", square: lineAnchor };
        return;
      }
      if (bestBeltRoute(state.position, state.industry, lineAnchor, square, localSide)) {
        // The reducer lays the legal prefix and stops; the anchor was checked
        // when it was set, so at least one part always lands.
        core.send({ type: "lay-belt-line", from: lineAnchor, to: square, owner: localSide });
        core.lineAnchor = undefined;
        return;
      }
      core.lineAnchor = undefined;
    } else if (lineAnchor !== undefined) {
      core.lineAnchor = undefined;
    }

    // A knight is selected and this is a pawn it can turn. This must beat
    // both "move there" and "select that pawn", which is why it is tested first.
    // In check nothing is lit (the reducer refuses a re-aim), but the click
    // still says why rather than quietly selecting the pawn.
    if (
      core.selected !== undefined &&
      state.status === "check" &&
      reAimTargetsOf(state, core.selected, core.controlsTurn).includes(square)
    ) {
      core.setRefusal("Re-aim is illegal while in check — you must answer it.");
      return;
    }
    if (core.selected !== undefined && d.reAimTargets.includes(square)) {
      core.pending = { kind: "re-aim", knight: core.selected, pawn: square };
      return;
    }

    // **By the square the move is *named* by**, which for a bash is the piece
    // being hit rather than where the attacker stops.
    const target = d.movesFromSelected.find((m) => moveTarget(m) === square);
    if (target !== undefined) {
      // Promotion piece is omitted: the reducer defaults to a queen. **Not
      // cleared.** The follow re-anchors to where the piece landed.
      core.send(moveCommand(state, moveFrom(target), square));
      return;
    }

    const piece = pieceAt(state.position, square);
    if (piece !== 0) {
      // Clicking a piece abandons a half-drawn run.
      core.lineAnchor = undefined;

      // What is on this square, in a fixed order. A repeat click steps to
      // the next one; a click elsewhere starts over.
      const layers: ("piece" | "belt")[] = ["piece"];
      if (this.belts.myBelt(square)) layers.push("belt");
      const step = core.cycle && core.cycle.square === square ? (core.cycle.index + 1) % layers.length : 0;
      core.cycle = { square, index: step };

      if (layers[step] === "belt") {
        // Re-pointing is turn-bound (§29 U1), so the picker does not open off-turn.
        if (!controlsTurn) {
          core.setRefusal(REFUSAL["not-your-turn"]);
          return;
        }
        core.selected = undefined;
        core.pending = { kind: "rotate", square };
        return;
      }

      // Selecting is looking, not acting: an enemy piece selected shows what
      // it is doing and no controls. **The piece says its name, quietly**, and
      // only when the selection *changes*.
      if (core.selected !== square) {
        core.cue({ kind: "voice", piece: PIECE_NAME[pieceType(piece)] });
        // **Your own piece, and it may not move** (§31 T2, T4; §33 T1 — its
        // pool spent, or it moved this turn): nothing lights, so say why
        // once, on the selection changing.
        if (controlsTurn && pieceColor(piece) === localSide) {
          const closed = pieceMoveRefusal(state, square);
          if (closed) core.setRefusal(closed);
        }
      }
      core.selected = square;
      return;
    }
    core.cycle = undefined;

    // A building. Not a piece and not a cell — but plainly a thing on the
    // board, so clicking it selects it exactly as clicking a piece does.
    if (hasStructure(state.industry, square)) {
      core.lineAnchor = undefined;
      this.voiceStructure(square);
      core.selected = square;
      return;
    }

    // Empty square. One of yours already there? Read it, and offer to
    // re-point it. Off-turn the card opens alone and nothing is refused.
    if (this.belts.myBelt(square)) {
      core.selected = square;
      core.lineAnchor = undefined;
      if (controlsTurn) core.pending = { kind: "rotate", square };
      return;
    }

    // **Letting go is a whole click of its own.** A click that means "never
    // mind" is only that; construction starts from a board with nothing
    // selected. What it guards is letting go of a *piece*: between two tiles
    // of ore there is no accidental construction to protect against.
    const inspectable = (sq: number) => groundModel(state, sq, localSide) !== null;
    if (core.selected !== undefined && !(inspectable(core.selected) && inspectable(square))) {
      core.selected = undefined;
      return;
    }

    // Bare ground, nothing selected, and no belt tool up. That is a click on
    // the board and nothing more. Quiet on purpose: nothing was refused.
    if (!core.building) {
      if (inspectable(square)) core.selected = square;
      return;
    }

    // The clock before the ground: laying is free of tempo and still waits
    // for your turn (§29 U1).
    if (!controlsTurn) {
      core.selected = undefined;
      core.setRefusal(REFUSAL["not-your-turn"]);
      return;
    }

    // The first click of a run, so the square has to be somewhere we could
    // actually start.
    const error = canPlaceBelt(state.position, state.industry, square, localSide);
    if (error !== null) {
      core.selected = undefined;
      core.setRefusal(PLACEMENT_REFUSAL[error]);
      return;
    }
    core.selected = undefined;
    core.lineAnchor = square;
    // Or a hover left over from the last anchor previews a run from this one
    // before the pointer has moved anywhere.
    core.hovered = undefined;
  }

  /** An arrow of the open picker was clicked: turn the pending click into a command. */
  pickDirection(direction: Direction, step?: boolean): void {
    const core = this.core;
    const pending = core.pending;
    if (!pending) return;
    if (pending.kind === "re-aim") {
      core.send({
        type: "re-aim",
        knight: pending.knight,
        pawn: pending.pawn,
        facing: direction,
        ...(step ? { step: true } : {})
      });
      core.selected = undefined;
    } else if (!core.controlsTurn) {
      // The picker only opens on your turn, and the opponent may have moved
      // while it stood open (§29 U1) — or the player has stepped back through
      // history with it open (§29 U4). Both belt arrows come through here.
      core.setRefusal(REFUSAL[actRefusal(core.state, core.localSide, core.viewing) ?? "not-your-turn"]);
      core.pending = undefined;
      core.changed();
      return;
    } else if (pending.kind === "manifold") {
      if (!core.send({ type: "play-card", instance: pending.instance, direction })) {
        const why = CARD_TABLE["spatial-manifold"].refuse(core.state, { direction });
        core.setRefusal(CARD_REFUSAL[why ?? ""] ?? "That card cannot be played.");
      } else core.cue({ kind: "card-played" });
    } else if (pending.kind === "lay") {
      core.send({ type: "lay-belt", square: pending.square, direction, owner: core.localSide });
    } else {
      core.send({ type: "rotate-belt", square: pending.square, direction, owner: core.localSide });
    }
    core.pending = undefined;
    core.changed();
  }

  cancelPending(): void {
    if (this.core.pending === undefined) return;
    this.core.pending = undefined;
    this.core.changed();
  }

  setMintFacing(facing: Direction | null): void {
    if (this.core.mintFacing === facing) return;
    this.core.mintFacing = facing;
    this.core.changed();
  }

  // --- what the board draws -----------------------------------------------

  /** Everything the old hook memoised, rebuilt once per revision. */
  private derived(): Derived {
    const core = this.core;
    if (this.memo && this.memo.revision === core.revision) return this.memo.derived;
    const { state, localSide, controlsTurn, viewing } = core;
    const drawing = this.belts.drawing();
    const movesFromSelected = selectedMoves(core.moves, core.selected, controlsTurn, localSide, state.position);
    // A re-aim spends the knight's own move and no pool (§33 T3): it lights
    // while that knight may still act, and is refused outside `playing` — in
    // check above all — so it lights nothing there either.
    const reAimTargets =
      core.selected !== null && core.selected !== undefined && state.status === "playing" && freeMoveRefusal(state, core.selected) === null
        ? reAimTargetsOf(state, core.selected, controlsTurn)
        : [];
    const lineEnds = lineEndsFrom(state, core.lineAnchor, localSide);
    const preview = beltPreviewFor(state, drawing, core.path, core.lineAnchor, core.hovered, localSide);
    // Free Space tiles waiting to go down. Derived, never stored.
    const paving = (state.run?.tiles ?? 0) > 0;
    const paveTargets = paving ? paveRing(state) : [];
    const aimMarks = aimMarksOf(state, core.aiming);
    const extent =
      core.selected === undefined ? NO_MARKS : (groundModel(state, core.selected, localSide)?.marks ?? NO_MARKS);
    // **Nothing is lit while looking back** (§29 U4): every mark is computed
    // against the live board and would be drawn over an earlier one.
    const highlights: BoardHighlights = viewing
      ? NO_HIGHLIGHTS
      : buildHighlights({
          selected: core.selected,
          movesFromSelected,
          reAimTargets,
          pending: core.pending,
          lineAnchor: core.lineAnchor,
          lineEnds,
          path: core.path,
          drawing,
          preview,
          forging: core.forging,
          placing: core.placing,
          hovered: core.hovered,
          aiming: core.aiming,
          aimMarks,
          paving,
          paveTargets,
          extent
        });
    const derived: Derived = {
      movesFromSelected,
      reAimTargets,
      paving,
      offBoard: paving || aimMarks.off.length > 0,
      highlights,
      drawing
    };
    this.memo = { revision: core.revision, derived };
    return derived;
  }

  view(): SessionView {
    const core = this.core;
    const d = this.derived();
    const pending = core.pending;
    const picker = pending ? (pending.kind === "re-aim" ? pending.pawn : pending.square) : undefined;
    const carry = this.carrying.carry();
    return {
      mode: this.mode(d),
      side: core.localSide as unknown as SessionView["side"],
      controlsTurn: core.controlsTurn,
      ...(core.selected === undefined ? {} : { selected: asSquare(core.selected) }),
      highlights: d.highlights,
      ...(picker === undefined
        ? {}
        : {
            picker: {
              square: asSquare(picker),
              remove: (pending?.kind === "lay" || pending?.kind === "rotate") && this.belts.myBelt(picker)
            }
          }),
      ghosts: d.highlights.beltPreview ?? [],
      refusal: core.refused,
      offBoard: d.offBoard,
      dismissable: this.dismissable(),
      carrying: this.carrying.carrying(),
      ...(carry ? { carry } : {}),
      ...this.chrome.view()
    };
  }

  private mode(d: Derived): SessionMode {
    const core = this.core;
    if (core.viewing) return "viewing";
    if (d.drawing) return "drawing";
    if (core.aiming || core.forging || core.placing || core.pending || core.lineAnchor !== undefined) return "arming";
    if (core.selected !== undefined) return "selected";
    return core.controlsTurn ? "idle" : "waiting";
  }

  /**
   * Is there a layer for `dismiss` to peel? **It mirrors `dismiss`'s own
   * order and must keep mirroring it**: a seventh layer added there without a
   * mention here makes Escape open the pause menu over a half-drawn run.
   */
  private dismissable(): boolean {
    const core = this.core;
    return Boolean(
      core.aiming ||
        core.forging ||
        core.placing ||
        core.pending ||
        core.lineAnchor !== undefined ||
        core.selected !== undefined
    );
  }

  /**
   * What a click means, as the shell's zones take it: the old hook's object,
   * key for key, so the chrome reads it unchanged until day 8 moves each zone
   * onto `view()`. Rebuilt per revision by the client's adapter.
   */
  interaction() {
    const core = this.core;
    const d = this.derived();
    const { state } = core;
    return {
      state,
      paving: d.paving,
      offBoard: d.offBoard,
      selected: core.selected,
      highlights: d.highlights,
      pickSquare: (square: number) => this.pickSquare(square),
      dismiss: () => this.dismiss(),
      dismissable: this.dismissable(),
      pickDirection: (direction: Direction) => this.pickDirection(direction),
      cancelPending: () => this.cancelPending(),
      toggleBelt: this.belts.toggleBelt,
      pending: core.pending,
      refusal: core.refused.text,
      refusalSaid: core.refused.said,
      actions: this.selection.actions(),
      act: this.selection.act,
      actionsFor: this.selection.actionsFor,
      doubleClickSquare: this.selection.doubleClickSquare,
      armForge: this.arming.armForge,
      mintFacing: core.mintFacing,
      setMintFacing: (facing: Direction | null) => this.setMintFacing(facing),
      upgradePiece: this.arming.upgradePiece,
      armBuild: this.arming.armBuild,
      forging: core.forging,
      placing: core.placing,
      beginPlace: this.arming.beginPlace,
      carrying: this.carrying.carrying(),
      carry: this.carrying.carry(),
      canCarry: this.carrying.canCarry,
      carryPiece: this.carrying.carryPiece,
      carryForge: this.carrying.carryForge,
      carryBuild: this.carrying.carryBuild,
      carryToken: this.carrying.carryToken,
      dropCarry: this.carrying.dropCarry,
      removeBelt: this.belts.removeBelt,
      discard: this.discard_,
      playCard: this.arming.playCard,
      aiming: core.aiming,
      cancelAiming: () => {
        core.aiming = undefined;
        core.changed();
      },
      beginSacrifice: this.arming.beginSacrifice,
      removePicked: this.belts.removePicked,
      startPath: this.belts.startPath,
      extendPath: this.belts.extendPath,
      commitPath: this.belts.commitPath,
      hover: (square: number) => this.hover(square),
      localSide: core.localSide,
      viewing: core.viewing,
      viewFlash: core.refused.text === REFUSAL["viewing-history"] ? core.refused.said : 0,
      send: (command: Parameters<SessionCore["send"]>[0]) => core.send(command),
      sideToMove: state.position.turn === WHITE ? "White" : "Black"
    };
  }
}

interface Derived {
  movesFromSelected: ReturnType<typeof selectedMoves>;
  reAimTargets: number[];
  paving: boolean;
  offBoard: boolean;
  highlights: BoardHighlights;
  drawing: boolean;
}

/** The shape the chrome reads. Inferred, like the hook's was. */
export type Interaction = ReturnType<Session["interaction"]>;

/** A session over a store: the core, the class, and its store subscription. */
export function createSession(store: GameStore, emit: SessionEmit): Session {
  return new Session(new SessionCore(store, emit));
}
